import { Column } from '@orion-platform/datagrid';
import { EnumVendorsFieldStructure, VendorsListField } from '../models/vendors-list.model';
import { VENDORS_COLUMN_LIST, VENDORS_FIELDS_KEY } from '../pages/vendors-list/vendors-list.consts';
import { ColumnsState } from '../types/columns.types';
import { ColumnsService } from './columns.service';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigService } from '@orion-platform/core';
import { environment } from '@orion/environment';
import { ApiQueryParams, QueryPayload } from 'apps/orion/src/app/models/app-config.model';
import { Observable } from 'rxjs';
import { RequestStatus } from '../models/commit.model';

import { DeleteBuyersContacts } from '../models/partnumbers-list.model';
import { createVendor, VendorsContacts } from '../models/vendors-list.model';
import { FormSetting } from '../store/carriers-list/carriers-list.types';
import {
  CreateBuyersContact,
  IdentityByKey,
  KeyValue,
  NotificationEmail,
  PartNumberContacts,
  PNSectionNameByContact,
  PNVCSectionNamePayload,
  PNVCSectionNameResponse,
  UpdateVendor,
  VendorListResponse,
  VendorsDetails,
} from '../store/vendors-list/vendors-list.types';
import { formatParams, generateHttpParams, getUrl } from '../utils/general.utils';
import { DefaultFormSettingsService } from './default-form-settings.service';
import { FileUploadService } from './file-upload.service';

@Injectable({
  providedIn: 'root',
})
export class VendorsListService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly fileUpload = inject(FileUploadService);
  private readonly defaultForms = inject(DefaultFormSettingsService);
  private readonly columns = inject(ColumnsService);

  /**
   * The plant's allowed fields, merged with whatever the user saved.
   * @returns {Observable<ColumnsState>} The columns, and whether they are the defaults.
   */
  getDefaultColumns = (): Observable<ColumnsState> =>
    this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new VendorsListField(EnumVendorsFieldStructure.VendorTable, formFields).fields as Column[],
      columnListKey: VENDORS_COLUMN_LIST,
      formFields$: this.getDefaultFormSettingFields(VENDORS_FIELDS_KEY),
    });

  private readonly mm = environment.modulesBaseUrl.materialManagement;
  private readonly baseUrl = this.config.getSettings('apiBaseUrl') as string;

  getVendorsList(data: ApiQueryParams): Observable<VendorListResponse> {
    const url = getUrl(this.baseUrl, this.mm.vendors);
    const params = formatParams(data);
    return this.http.get<VendorListResponse>(url, { params });
  }

  getVendors(): Observable<VendorListResponse> {
    const url = getUrl(this.baseUrl, this.mm.vendors);
    return this.http.get<VendorListResponse>(url);
  }

  createVendorsList(vendor: createVendor): Observable<VendorsDetails> {
    const url = getUrl(this.baseUrl, this.mm.vendors);
    return this.http.post<VendorsDetails>(url, vendor);
  }

  updateVendorsList(vendorUpdate: UpdateVendor): Observable<VendorsDetails> {
    const url = getUrl(this.baseUrl, this.mm.vendors);
    return this.http.put<VendorsDetails>(`${url}/${vendorUpdate.id}`, vendorUpdate);
  }

  deleteVendors(id: string): Observable<VendorsDetails> {
    const url = getUrl(this.baseUrl, this.mm.vendors);
    return this.http.delete<VendorsDetails>(`${url}/${id}`);
  }

  createVendorWithFlag(data: createVendor): Observable<VendorsDetails> {
    const url = getUrl(this.baseUrl, this.mm.vendors);
    return this.http.post<VendorsDetails>(`${url}/createvendorwithflag`, data);
  }

  uploadVendorsList(fileName: string, file: File): Observable<HttpEvent<RequestStatus>> {
    const url = getUrl(this.baseUrl, this.mm.uploadVendorList);
    return this.fileUpload.uploadFormData(url, fileName, file);
  }

  batchEditVendorRequestStatus(requestHash: string): Observable<RequestStatus> {
    const url = getUrl(this.baseUrl, this.mm.uploadVendorList);
    return this.http.get<RequestStatus>(`${url}/-/RequestStatus/${requestHash}`);
  }

  downloadVendorsList(vendorsList: QueryPayload): Observable<Blob> {
    const url = getUrl(this.baseUrl, this.mm.downloadVendorList);
    return this.http.post(url, vendorsList, { responseType: 'blob' });
  }

  getIdentityByKey(key: string): Observable<IdentityByKey> {
    const url = getUrl(this.baseUrl, this.mm.getIdentityByKey);
    const params = generateHttpParams({ key });
    return this.http.get<IdentityByKey>(url, { params });
  }

  getVendorsContacts(data: KeyValue): Observable<VendorsContacts[]> {
    const url = getUrl(this.baseUrl, this.mm.contacts);
    const params = generateHttpParams({ key: data.key, value: data.value });
    return this.http.get<VendorsContacts[]>(url, { params });
  }

  getPartNumberSectionName(vendorCode: string): Observable<PNSectionNameByContact[]> {
    const url = getUrl(this.baseUrl, this.mm.pnSectionName);
    const params = generateHttpParams({ vendorCode });
    return this.http.get<PNSectionNameByContact[]>(url, { params });
  }

  setPartNumberVendorCodeSectionName(
    payload: PNVCSectionNamePayload,
  ): Observable<PNVCSectionNameResponse> {
    const url = getUrl(this.baseUrl, this.mm.setPartNumberVendorCodeSectionName);
    return this.http.post<PNVCSectionNameResponse>(url, payload);
  }

  deletePartNumberVendorCodeSectionName(
    data: PNVCSectionNamePayload,
  ): Observable<PNVCSectionNameResponse> {
    const url = `${getUrl(this.baseUrl, this.mm.partNumbers)}/DeletePartNumberVendorCodeSectionName/${data.partNumber}/${data.vendorCode}`;
    return this.http.delete<PNVCSectionNameResponse>(url);
  }

  createBuyersContacts(buyer: CreateBuyersContact): Observable<CreateBuyersContact> {
    const baseUrl = getUrl(this.baseUrl, this.mm.partNumbers);
    return this.http.post<CreateBuyersContact>(`${baseUrl}/createcontact`, buyer);
  }

  deleteBuyersContacts(buyer: DeleteBuyersContacts): Observable<DeleteBuyersContacts> {
    const baseUrl = getUrl(this.baseUrl, this.mm.partNumbers);
    return this.http.post<DeleteBuyersContacts>(`${baseUrl}/deletecontact`, buyer);
  }

  getBuyersContacts(data: QueryPayload): Observable<PartNumberContacts[]> {
    const baseUrl = getUrl(this.baseUrl, this.mm.partNumbers);
    const params = generateHttpParams({ $filter: data.$filter ?? '' });
    return this.http.get<PartNumberContacts[]>(`${baseUrl}/getgroupcontacts`, {
      params,
    });
  }

  getNotificationEmails(data: KeyValue): Observable<NotificationEmail[]> {
    const baseUrl = getUrl(this.baseUrl, this.mm.Notification);
    const params = generateHttpParams(data);
    return this.http.get<NotificationEmail[]>(`${baseUrl}/GetNotificationEmails`, {
      params,
    });
  }

  updateNotificationEmails(notification: NotificationEmail): Observable<NotificationEmail> {
    const baseUrl = getUrl(this.baseUrl, this.mm.Notification);
    return this.http.post<NotificationEmail>(
      `${baseUrl}/CreateOrUpdateNotificationEmails`,
      notification,
    );
  }

  deleteNotificationEmails(notification: NotificationEmail): Observable<NotificationEmail> {
    const baseUrl = getUrl(this.baseUrl, this.mm.Notification);
    return this.http.post<NotificationEmail>(`${baseUrl}/DeleteNotificationEmails`, notification);
  }

  getVendorsByContacts(data: ApiQueryParams, payload: string[]): Observable<VendorListResponse> {
    const baseUrl = getUrl(this.baseUrl, this.mm.getVendorsByContacts);
    const params = formatParams(data);
    const url = `${baseUrl}?${params.toString()}`;
    return this.http.post<VendorListResponse>(url, payload);
  }

  getDefaultFormSettingFields(formKey: string): Observable<string[]> {
    const url = getUrl(this.baseUrl, this.mm.vendors);
    return this.defaultForms.getFields(url, formKey);
  }

  getDefaultFormSettingFormKeys(): Observable<string[]> {
    const url = getUrl(this.baseUrl, this.mm.vendors);
    return this.defaultForms.formKeys(url);
  }

  getDefaultFormSettings(formKey: string): Observable<FormSetting[]> {
    const url = getUrl(this.baseUrl, this.mm.vendors);
    return this.defaultForms.getFormSettings(url, formKey);
  }

  updateDefaultFormSettings(formKey: string, payload: FormSetting[]): Observable<FormSetting[]> {
    const url = getUrl(this.baseUrl, this.mm.vendors);
    return this.defaultForms.update(url, formKey, payload);
  }

  /**
   * Which fields the create form shows, as the user arranged them.
   *
   * The same list in the same shape as a grid's columns, stored the same way —
   * only what draws it differs.
   * @returns {Observable<ColumnsState>} The fields, and whether they are still the defaults.
   */
  getFormFieldColumns = (): Observable<ColumnsState> =>
    this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new VendorsListField(EnumVendorsFieldStructure.VendorCreateForm, formFields)
          .fields as unknown as Column[],
      columnListKey: 'vendors_field_list',
      formFields$: this.getDefaultFormSettingFields('VENDORS_DETAIL_FORMKEY'),
    });
}
